import { ReactElement, useCallback, useEffect, useRef, useState } from "react";
import 'chartjs-adapter-moment';
import zoomPlugin from 'chartjs-plugin-zoom'
import { Chart, ChartData, ChartOptions, TimeUnit } from "chart.js";
import { useDependency } from "../../../contexts/DependencyContext";
import { ChartUnitService, ChartSize } from "../../../services/chart/ChartUnitService";
import annotationPlugin from 'chartjs-plugin-annotation';

Chart.register(annotationPlugin);

export interface ILineChartSeries
{
    title: string,
    labels: string[],
    values: number[],
    yAxisID: string
}

export const ChartPointsPerPixelRatio = 1;

export interface ILineChartProps
{
    size: ChartSize,
    series: ILineChartSeries[],
    enableZoom: boolean,
    displayLegend: boolean,
    xLabel?: string,
    yLabel?: string,
    y1Label?: string,
    y2Label?: string,
    yAxisID?: string,
    timeUnit: TimeUnit,
    minXBar?: number,
    maxXBar?: number,
    unit?: number,
    maxY: number,
    xFlowRate?: number,
    getTooltipLabel?: (context: any) => string | string[]
    getTooltipTitle?: (tooltipItems: any[]) => string
}

const LineChartUnit = (props: ILineChartProps): ReactElement =>
{
    const service = useDependency(ChartUnitService);
    const [lastProps, setLastProps] = useState<ILineChartProps | null>(null);
    const lineChart = useRef<ReactElement>(<></>);

    const havePropsChanged = useCallback((): boolean => {
        let propsHaveChanged = false;
        if (!lastProps) propsHaveChanged = true;
        if (!propsHaveChanged && lastProps) propsHaveChanged = Object.keys(props).length !== Object.keys(lastProps).length;

        if (!propsHaveChanged && lastProps) {
            for (let i = 0; i < Object.values(props).length; i++) {
                const propVal = Object.values(props)[i];
                const lastPropVal = Object.values(lastProps)[i];
                propsHaveChanged = propVal !== lastPropVal;

                if (propsHaveChanged) {
                    break
                };
            }
        }
        return propsHaveChanged;
    }, [props, lastProps])

    function getData(series: ILineChartSeries[]): ChartData {
        return {

            labels: series.flatMap(x => x.labels).filter((value, index, self) => self.indexOf(value) === index),
            datasets: series.map((x, i) =>
            {
                let color;
                switch(i)
                {
                    case 0:
                        color = 'rgba(35,122,235,0.6)';
                        break;
                    case 1:
                        color = 'rgba(235,122,35,0.6)';
                        break;
                    case 2:
                        color = 'rgba(30, 169, 138, 1)';
                        break;
                }

                return {
                    label: x.title,
                    data: x.values,
                    fill: false,
                    backgroundColor: color,
                    borderColor: color,
                    pointBackgroundColor: color,
                    borderWidth: 1,
                    pointBorderWidth: 0.5,
                    pointRadius: 0.5,
                    tension: 0.5,
                    yAxisID: x.yAxisID
                }
            })
        }
    }

    const getOptions = useCallback((options: ILineChartProps): ChartOptions =>
    {
        const chartOptions = JSON.parse(JSON.stringify(service.defaultOptions));
        const { timeUnit, xLabel, yLabel, y1Label, y2Label,displayLegend, getTooltipLabel, getTooltipTitle, enableZoom } = options;

        if (chartOptions?.scales)
        {
            if (chartOptions.scales.x && timeUnit)
            {
                chartOptions.scales.x.time = { unit: timeUnit };
                if (timeUnit === "day") chartOptions.scales.x.time.displayFormats = { 'day': 'MMM DD' };
            }
            if (xLabel && chartOptions.scales.x && chartOptions.scales.x.title)
            {
                chartOptions.scales.x.title.text = xLabel
            };

            if (yLabel)
            {
                if (chartOptions.scales.y.title) chartOptions.scales.y.title.text = yLabel;
            }

            if (y1Label)
            {
                if (chartOptions.scales.y1.title) chartOptions.scales.y1.title.text = y1Label;
            }

            if (y2Label)
            {
                if (chartOptions.scales.y2.title) chartOptions.scales.y2.title.text = y2Label;
            }
        }

        if (chartOptions?.plugins?.legend)
            chartOptions.plugins.legend.display = displayLegend;

        if (getTooltipLabel && chartOptions.plugins?.tooltip?.callbacks)
        {
            chartOptions.plugins.tooltip.callbacks.label = getTooltipLabel;
        }

        if (getTooltipTitle && chartOptions.plugins?.tooltip?.callbacks)
            chartOptions.plugins.tooltip.callbacks.title = getTooltipTitle;

        if(chartOptions?.plugins?.annotation && props.unit && props.maxXBar && props.minXBar && props.xFlowRate)
        {
            chartOptions.scales.y.max = props.maxY;
            chartOptions.plugins.annotation.annotations.box1 = {type: 'box', xMin: props.minXBar / props.unit, xMax: props.maxXBar / props.unit,
                                                                yMin: 0, yMax: props.maxY, backgroundColor: 'rgba(253, 213, 181, 0.25)', borderColor: 'rgba(253, 213, 181, 0.25)',
                                                                borderShadowColor: 'rgba(253, 213, 181, 0.25)'}
            chartOptions.plugins.annotation.annotations.line1 = {type: 'line', yMin: 0, yMax: props.maxY, borderColor: 'rgb(103, 102, 99)',
                                                                 xMin: props.xFlowRate / props.unit, xMax: props.xFlowRate / props.unit ,borderWidth: 1, borderDash: [10, 10]}
        }
        if (enableZoom)
        {
            Chart.register(zoomPlugin);
            chartOptions.plugins.zoom =
            {
                pan: {
                    enabled: true,
                    mode: "xy",
                },
                zoom: {
                    mode: "xy",
                    wheel: {
                        enabled: true
                    },
                    pinch: {
                        enabled: true
                    },
                    onZoomStart({ chart }: { chart: Chart })
                    {
                        const zoom = chart?.config?.options?.plugins?.zoom?.zoom ? chart.config.options.plugins.zoom.zoom : null;
                        if (zoom && zoom.mode)
                        {
                            zoom.mode = service.getZoom();
                        };
                    },
                },
            }
        }

        return chartOptions;
    }, [service])

    function getHeight(size: ChartSize): number
    {
        switch (size) {
            case 'large':
                return 500;

            case 'small':
                return 300;

            default:
                return 400;
        }
    }

    useEffect(() => {
        if (havePropsChanged()) {
            lineChart.current = service.setChart(
                getData(props.series),
                getOptions(props),
                getHeight(props.size)
            );
            const shouldSetEventListener = true;
            if (shouldSetEventListener) service.setEventListeners();
            setLastProps(props);
        }
    }, [service, havePropsChanged,getOptions, props])

    return (
        <>
            {lineChart.current}
        </>
    )
}

export default LineChartUnit;